import { Component } from 'react';

import AnimateHeight from '@skyscanner/backpack-web/bpk-animate-height';
import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import { withButtonAlignment } from '@skyscanner/backpack-web/bpk-component-icon';
import SearchIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/search';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import SearchControls, { LAYOUT } from '../SearchControls';

import SearchBarHotelStay from './SearchBarHotelStay';

import type { SearchSuggestion } from '../types';
import type { I18nService } from '@shared/types/i18n';
import type { FormatDate } from '@shared/types/i18n/localisation-service';
import type { Stay } from '@shared/types/utils';
import type { Destination } from '@shared/ui/types';

import STYLES from './SearchBar.module.scss';

import { withI18n } from '@shared/ui/contexts/i18n';

const cls = cssModules(STYLES);
const AlignedSearchIcon = withButtonAlignment(SearchIcon);

export type SearchControlsSubmitParams = {
  checkIn: Date;
  checkOut: Date;
  childrenAges: number[];
  destination: Destination | SearchSuggestion;
  numberOfAdults: number;
  numberOfRooms: number;
};

type Props = {
  i18n: I18nService;
  stay: Stay;
  onSearchControlsSubmit: (
    params: SearchControlsSubmitParams,
    formatDate: FormatDate,
  ) => void;
  isMobile: boolean;
  destination?: Destination;
  className?: string;
  arrangeInline?: boolean;
  onChangeExpaned?: () => void;
  isFromSeoPage?: boolean;
};

type State = {
  expanded: boolean;
};

const defaultProps = {
  arrangeInline: false,
};

class SearchBar extends Component<Props, State> {
  static defaultProps = defaultProps;

  constructor(props: Props) {
    super(props);

    const { arrangeInline } = props;

    this.state = {
      expanded: !!arrangeInline,
    };
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      prevProps.isMobile &&
      !prevState.expanded &&
      this.props.isMobile !== prevProps.isMobile
    ) {
      this.setState({ expanded: true });
    }
  }

  onToggle = () => {
    const { expanded } = this.state;
    this.setState({ expanded: !expanded });
    if (this.props.onChangeExpaned) {
      this.props.onChangeExpaned();
    }
  };

  onSearchControlsSubmit = (params: SearchControlsSubmitParams) => {
    const {
      i18n: { formatDate },
    } = this.props;
    const { onSearchControlsSubmit } = this.props;
    onSearchControlsSubmit(params, formatDate);
  };

  destinationLabel = () => {
    const {
      i18n: { translate },
    } = this.props;

    return translate('SearchControls_label_whereDoYouWantToStay');
  };

  render() {
    const {
      arrangeInline,
      className,
      destination,
      i18n: { translate },
      isFromSeoPage,
      isMobile,
      stay,
    } = this.props;

    const { entity: destinationName } = destination || {};
    const classNames = [STYLES.SearchBar, className];

    return (
      <>
        {isMobile ? (
          <section className={classNames.join(' ')}>
            <div
              className={cls(
                'SearchBar__searchControls',
                'SearchBar__searchControlsExpand',
              )}
            >
              <SearchControls
                stay={stay}
                destination={destination}
                onSubmit={this.onSearchControlsSubmit}
                shouldAutoSearch
                layout={LAYOUT.HORIZONTAL}
                destinationLabel={this.destinationLabel()}
                hideLabel
              />
            </div>
          </section>
        ) : (
          <div className={classNames.join(' ')}>
            {!arrangeInline && (
              <div
                role="button"
                className={STYLES.SearchBar__staticInfo}
                onClick={this.onToggle}
                onKeyPress={this.onToggle}
                tabIndex={0}
                data-test-id="search-bar"
              >
                <div className={STYLES.SearchBar__searchButton}>
                  <BpkButtonV2 iconOnly type={BUTTON_TYPES.featured}>
                    <AlignedSearchIcon />
                    <span className="visually-hidden">
                      {translate('Button_search')}
                    </span>
                  </BpkButtonV2>
                </div>
                <div className={STYLES.SearchBar__info}>
                  <div
                    className={STYLES.SearchBar__destination}
                    data-test-id="destination"
                  >
                    {destinationName}
                  </div>
                  <SearchBarHotelStay stay={stay} preferHorizontal />
                </div>
              </div>
            )}

            <AnimateHeight
              duration={200}
              height={this.state.expanded ? 'auto' : 0}
            >
              <div
                className={cls(
                  'SearchBar__searchControls',
                  arrangeInline && 'SearchBar__searchControlsExpand',
                )}
              >
                <SearchControls
                  stay={stay}
                  destination={destination}
                  arrangeInline={arrangeInline}
                  showPreference={!arrangeInline}
                  onSubmit={this.onSearchControlsSubmit}
                  destinationLabel={this.destinationLabel()}
                  isFromSeoPage={isFromSeoPage}
                />
              </div>
            </AnimateHeight>
          </div>
        )}
      </>
    );
  }
}

export default withI18n(SearchBar);
